/**
 * Maps discovery `value_formats` (per-finding date-format / name-order distributions) onto the
 * flat masking params the worker consumes (`dateFormat`, `nameOrder`) and the algorithm-alias
 * override those distributions imply.
 *
 * A `value_formats` block is keyed by finding type:
 *   { <findingType>: { kind: 'date'|'name_order', counts: {...}, dominant: <string|null> } }
 *
 * Date-format padding rationale
 * -----------------------------
 * The model's date masks (e.g. `MM/DD/YYYY`) describe the *shape* of the value but NOT whether
 * the day/month components are zero-padded — `3/7/2024` and `03/07/2024` both classify as
 * `MM/DD/YYYY`. Java's DateTimeFormatter is strict about field width: pattern `MM` refuses to
 * parse a single-digit month and `dd` refuses a single-digit day. A parse failure inside the
 * masking-service date algorithm returns the value UNMASKED — i.e. it silently leaks PII. We
 * therefore map to SINGLE-LETTER day/month tokens (`M`/`d`), which accept both padded and
 * unpadded input, while keeping the literal separators and the 4-vs-2 digit year width
 * (`yyyy`/`yy`) verbatim. Month names use `MMM`/`MMMM`.
 */

// Date-only masks → strict-safe Java DateTimeFormatter patterns. `YYYY-MM-DD` is intentionally
// absent (the worker auto-detects ISO-8601 without a hint), as is anything carrying a time
// suffix — both fall through the exact-match lookup to null. See dateFormatParamFor.
const DATE_MASK_TO_JAVA_PATTERN = {
  'MM/DD/YYYY': 'M/d/yyyy',
  'DD/MM/YYYY': 'd/M/yyyy',
  'YYYY/MM/DD': 'yyyy/M/d',
  'MM-DD-YYYY': 'M-d-yyyy',
  'MM.DD.YYYY': 'M.d.yyyy',
  'MM/DD/YY': 'M/d/yy',
  YYYYMMDD: 'yyyyMMdd',
  'DD Mon YYYY': 'd MMM yyyy',
  'DD Month YYYY': 'd MMMM yyyy',
  'Mon DD, YYYY': 'MMM d, yyyy',
  'Mon. DD, YYYY': 'MMM. d, yyyy',
  'DD-Mon-YYYY': 'd-MMM-yyyy',
};

/**
 * Java DateTimeFormatter pattern for a discovery date-format mask, or null.
 * Exact-match lookup only: masks with a time suffix (`MM/DD/YYYY HH:mm:ss`, `YYYY-MM-DDTHH:mm`),
 * ISO `YYYY-MM-DD`, and anything unrecognised are absent from the table and return null — the
 * worker auto-detects those. Returns null for null/undefined.
 */
export function dateFormatParamFor(dominantMask) {
  if (dominantMask == null) return null;
  const mapped = DATE_MASK_TO_JAVA_PATTERN[String(dominantMask)];
  return mapped != null ? mapped : null;
}

/** The value_formats entry for a finding type, or null. */
function formatEntryFor(valueFormats, findingType) {
  if (!valueFormats || typeof valueFormats !== 'object' || !findingType) return null;
  const entry = valueFormats[findingType];
  return entry && typeof entry === 'object' ? entry : null;
}

/**
 * Flat masking params recommended by a field's value_formats, keyed on `findingType` (the same
 * top-finding entity used to pick the recommended algorithm). Returns `{}` when nothing applies.
 *  - kind 'date' with a mappable dominant → { dateFormat: <java pattern> } (omitted if unmappable)
 *  - kind 'name_order' dominant 'LAST_COMMA_FIRST' → { nameOrder: 'LAST_COMMA_FIRST' }
 *  - kind 'name_order' dominant 'LAST_FIRST'       → { nameOrder: 'LAST_FIRST' }
 *  - kind 'name_order' dominant 'FIRST_LAST' / null → {} (the default order needs no param)
 */
export function recommendedParamsFor(valueFormats, findingType) {
  const entry = formatEntryFor(valueFormats, findingType);
  if (!entry) return {};
  if (entry.kind === 'date') {
    const dateFormat = dateFormatParamFor(entry.dominant);
    return dateFormat ? { dateFormat } : {};
  }
  if (entry.kind === 'name_order') {
    if (entry.dominant === 'LAST_COMMA_FIRST') return { nameOrder: 'LAST_COMMA_FIRST' };
    if (entry.dominant === 'LAST_FIRST') return { nameOrder: 'LAST_FIRST' };
    return {};
  }
  return {};
}

/**
 * Algorithm-alias override implied by a field's value_formats, or null when the base recommendation
 * stands. A `LAST_FIRST` person column whose base recommendation is `PERSON` upgrades to the
 * `PERSON_LAST_FIRST` catalog alias. `LAST_COMMA_FIRST` keeps `PERSON` (comma detection is intrinsic
 * to PersonMasking; the order rides as a flat nameOrder param instead).
 */
export function recommendedAlgorithmOverride(valueFormats, findingType, baseRecommended) {
  const entry = formatEntryFor(valueFormats, findingType);
  if (!entry) return null;
  if (
    entry.kind === 'name_order' &&
    entry.dominant === 'LAST_FIRST' &&
    String(baseRecommended || '').toUpperCase() === 'PERSON'
  ) {
    return 'PERSON_LAST_FIRST';
  }
  return null;
}
